import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { SocketService } from './socket.service';
import { ToastrService } from 'ngx-toastr';

export interface TimeLog {
  _id?: string;
  userId: string;
  projectId: string;
  taskId: string;
  date: Date;
  startTime?: Date;
  endTime?: Date;
  duration: number; // in seconds
  description?: string;
  isBillable: boolean;
  loggedBy: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CreateTimeLogPayload {
  taskId: string;
  projectId: string;
  date?: Date;
  startTime?: Date;
  endTime?: Date;
  duration: number;
  description?: string;
  isBillable?: boolean;
  userIds?: string[]; // For collaborative logging
}

@Injectable({
  providedIn: 'root',
})
export class TimelogService {
  private baseUrl = environment.baseUrl;
  private timeLogCreatedSubject = new Subject<any>();
  private openModalSubject = new Subject<string | undefined>();

  // Observable for components to subscribe to timelog creation events
  timeLogCreated$ = this.timeLogCreatedSubject.asObservable();

  // Observable for components to subscribe to modal open events
  openModal$ = this.openModalSubject.asObservable();

  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private toastr: ToastrService,
  ) {
    this.initSocketListeners();
  }

  /**
   * Initialize socket event listeners for real-time timelog updates
   */
  private initSocketListeners(): void {
    // Listen for timelog created events
    this.socketService.on('timelog:created', (data: any) => {
      this.handleTimeLogCreated(data);
    });

    // Listen for timelog updated events
    this.socketService.on('timelog:updated', (data: any) => {
      this.handleTimeLogUpdated(data);
    });

    // Listen for timelog deleted events
    this.socketService.on('timelog:deleted', (data: any) => {
      this.handleTimeLogDeleted(data);
    });
  }

  /**
   * Handle timelog created socket event
   */
  private handleTimeLogCreated(data: any): void {
    const { timelog, createdBy } = data;

    // Get current user to check if this log was created by someone else
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    // Only show notification if someone else logged time for current user
    if (
      timelog?.userId?._id === currentUser._id &&
      createdBy?._id !== currentUser._id
    ) {
      const creatorName = createdBy?.fullName || 'Someone';
      const taskName = timelog?.taskId?.title || 'a task';
      const durationMinutes = Math.floor(timelog.duration / 60);
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      const durationText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

      this.toastr.info(
        `${creatorName} logged ${durationText} for you on "${taskName}"`,
        'Time Logged',
        {
          timeOut: 5000,
          progressBar: true,
        },
      );
    }

    // Emit event for components to react
    this.timeLogCreatedSubject.next(data);
  }

  /**
   * Handle timelog updated socket event
   */
  private handleTimeLogUpdated(data: any): void {
    console.log('TimeLog updated:', data);
    // Emit event for components to react
    this.timeLogCreatedSubject.next(data);
  }

  /**
   * Handle timelog deleted socket event
   */
  private handleTimeLogDeleted(data: any): void {
    console.log('TimeLog deleted:', data);
    // Emit event for components to react
    this.timeLogCreatedSubject.next(data);
  }

  /**
   * Create a time log (supports multi-user collaborative logging)
   */
  createTimeLog(payload: CreateTimeLogPayload): Observable<any> {
    return this.http.post(`${this.baseUrl}time-logs`, payload);
  }

  /**
   * Get time logs for a specific task
   */
  getTimeLogsByTask(taskId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}time-logs/task/${taskId}`);
  }

  /**
   * Get time logs for a specific user
   */
  getTimeLogsByUser(
    userId: string,
    startDate?: Date,
    endDate?: Date,
  ): Observable<any> {
    let url = `${this.baseUrl}time-logs?userId=${userId}`;
    if (startDate) {
      url += `&startDate=${startDate.toISOString()}`;
    }
    if (endDate) {
      url += `&endDate=${endDate.toISOString()}`;
    }
    return this.http.get(url);
  }

  /**
   * Get time logs for a specific project
   */
  getTimeLogsByProject(projectId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}time-logs/project/${projectId}`);
  }

  getTimeLogTotalsByProject(projectId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}time-logs/project/${projectId}/totals`);
  }

  /**
   * Get all time logs with pagination, filtering, and sorting
   */
  getAllTimeLogs(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}time-logs/get-all`, payload);
  }

  /**
   * Update a time log
   */
  updateTimeLog(id: string, payload: Partial<TimeLog>): Observable<any> {
    return this.http.put(`${this.baseUrl}time-logs/${id}`, payload);
  }

  /**
   * Delete a time log
   */
  deleteTimeLog(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}time-logs/${id}`);
  }

  /**
   * Format duration in seconds to human-readable format
   */
  formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  /**
   * Convert hours and minutes to total seconds
   */
  convertToSeconds(hours: number, minutes: number): number {
    return hours * 3600 + minutes * 60;
  }

  /**
   * Convert seconds to hours and minutes
   */
  convertFromSeconds(seconds: number): { hours: number; minutes: number } {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return { hours, minutes };
  }

  /**
   * Get distinct users who have logged time (for filter dropdown)
   */
  getTimeLogUsers(): Observable<any> {
    return this.http.get(`${this.baseUrl}time-logs/filter-options/users`);
  }

  /**
   * Get distinct projects that have time logs (for filter dropdown)
   */
  getTimeLogProjects(): Observable<any> {
    return this.http.get(`${this.baseUrl}time-logs/filter-options/projects`);
  }

  /**
   * Get distinct tasks that have time logs (for filter dropdown)
   */
  getTimeLogTasks(): Observable<any> {
    return this.http.get(`${this.baseUrl}time-logs/filter-options/tasks`);
  }

  /**
   * Trigger the log time modal to open for a specific timer
   * Components should subscribe to openModal$ to handle this event
   */
  public openTimeLogModalForTimer(timerId?: string): void {
    this.openModalSubject.next(timerId);
  }
  /*
   * Get chart data (daily totals) for a specific month
   */
  getChartData(
    month: Date,
    userId?: string,
    timezone?: string,
  ): Observable<any> {
    const payload = {
      month: month,
      userId: userId,
      timezone: timezone,
    };
    return this.http.post(`${this.baseUrl}time-logs/chart-data`, payload);
  }

  /**
   * Timesheet export: the server builds the Excel or PDF file, uploads it to
   * S3, records it in timeLogs_reports, and returns { fileName, fileUrl, ... }.
   * Dates are YYYY-MM-DD in `timezone`; an empty memberIds means all members.
   */
  exportTimesheet(payload: {
    format: 'xlsx' | 'pdf';
    startDate: string;
    endDate: string;
    billable: 'both' | 'billable' | 'non-billable';
    memberIds: string[];
    timezone: string;
    projectId?: string;
    projectIds?: string[];
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}time-logs/export`, payload);
  }

  /** Projects for the export popup's Projects filter (all for admins, own projects otherwise). */
  getExportProjects(): Observable<any> {
    return this.http.get(`${this.baseUrl}time-logs/export/projects`);
  }

  /** People who logged time on the chosen projects in the period, for the export popup. */
  getExportMembers(payload: {
    projectIds: string[];
    startDate: string;
    endDate: string;
    timezone: string;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}time-logs/export/members`, payload);
  }
}
